'use strict';
namespace Quote {

    type IQuoteSearch = Quote.IQuoteSearch;

    export interface IAgentIdentity {
        Id: number;
        FullName: string;
        UserName: string;
    }

    export interface ITravelAgentIdentity {
        Id: number;
        Name: string;
        AgencyRef: string;
    }

    export interface IQuoteDateLimits {
        startMaxDate?: Date;
        endMinDate?: Date;
        endMaxDate?: Date;
    }

    class AdvancedQuoteSearch {
        public busy:boolean = false;
        private showSearchForm: boolean;
        public toggleSearchForm:boolean = true;
        private selectedAgent: IAgentIdentity | undefined;
        private selectedTravelAgent: ITravelAgentIdentity | undefined;
        private selectedCustomer: any | undefined;

        private searching: boolean;
        private noPermission: boolean;
        private quoteSearch: Quote.IQuoteSearch;
        private today: Date;
        private agents: IAgentIdentity[];
        private dateLimits: IQuoteDateLimits;
        private userDetails: any;
        private travelAgentDetails: any;

        public hideAdvancedSearch: () => void | undefined;
        public showTravelAgencyDetail: boolean| undefined;
        public showLoyaltyNumber: boolean | undefined;
        public showLinkedBookings: boolean | undefined;
        public allowToggleAdvancedSearch: boolean | undefined;

        constructor(
            private quoteService: Quote.QuoteService,
            private $scope: any,
            private resourceService: any,
            private agentModeService: any,
            private dateUtil: any,
            private $location: any,
            private broker: any,
            private $q: ng.IQService,
            private localStorageProvider: any
        ) {
            this.initSearch(quoteService.getCachedSearch());
        }

        searchAgain(): void {
            this.searching = false;
            this.showSearchForm = true;
            this.toggleSearchForm = true;
        }

        private initSearch(cache: IQuoteSearch | null): void {
            this.searching = false;
            this.showSearchForm = true;
            this.allowToggleAdvancedSearch = 'function' === typeof this.hideAdvancedSearch;
            this.today = this.dateUtil.todayPlus(0, 0, true);
            this.dateLimits = {
                startMaxDate: this.today,
                endMaxDate: this.dateUtil.todayPlus(1, 0, true)
            };
            this.toggleSearchForm = true;
            this.quoteSearch = {
                searchModel: {
                    domainId: TRPCONFIG.Domain.DomainId,
                    page: 1,
                    limit: 9,
                    sortDirection: 'DESC'
                },
                searchResponse: null
            };
            if(cache != null){
                this.quoteSearch = cache;
                this.showSearchForm = false;
                this.toggleSearchForm = false;
            }
            this.agentModeService.getCoworkers().then(data => { this.agents = data; });
        }

        search(pgIndex: number): ng.IPromise<void> {
            this.noPermission = false;
            this.quoteSearch.searchModel.page = pgIndex;
            this.quoteSearch.searchModel.reference = this.quoteSearch.searchModel.quoteNo
                ? `QUO/${this.quoteSearch.searchModel.quoteNo}`
                : '';
            this.searching = true;
            this.showSearchForm = false;

            const onRequestFinished = (searchResponse) => {
                this.searching = false;
                this.toggleSearchForm = false;
                this.quoteSearch.searchResponse = searchResponse;
            };

            return this.quoteService.search(this.quoteSearch.searchModel).then<void>(res => {
                onRequestFinished(res);
            }, err => {
                if(err.status === 400) {
                    this.showError('QUOTEADVANCESEARCH_NOTSPECIFIC');
                    this.searching = false;
                    this.showSearchForm = true;
                    return;
                }
                onRequestFinished(null);
                this.noPermission = 401 === err.status;
            });
        }

        private showError(error): void {
            this.$scope.$broadcast('flash:error', this.resourceService.get(error));
        }

        private page = (num: number, e: any): void => {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            this.search(num);
        }


        manageQuote(reference: string): void {
            this.broker.publish('quoteSelected', reference);
            this.localStorageProvider.setItem('quoteSelected', reference);
            this.$location.path('quote/manage/' + reference);
        }

        setStartDate(date: Date): void {
            this.quoteSearch.searchModel.from = date;
            this.dateLimits.endMinDate = date;
        }


        setEndDate(date: Date): void {
            this.quoteSearch.searchModel.to = date;
        }


        private selectAgent(agent: IAgentIdentity): void {
            if(agent) {
                this.quoteSearch.searchModel.quotedById = agent.Id;
                this.selectedAgent = agent;
            }
        }


		private selectCustomer(customer: any): void {
            if(customer) {
                this.selectedCustomer = customer;
                this.quoteSearch.searchModel.userId = customer.UserId;
            }
        }


        private selectTravelAgent(agent: ITravelAgentIdentity): void {
            if(agent) {
                this.selectedTravelAgent = agent;
                this.quoteSearch.searchModel.externalAgentId = agent.Id;
            }
        }


        private filterAgent(prefix: string): IAgentIdentity[] {
            return _.filter(this.agents, agent => _.includes(_.toLower(agent.FullName), _.toLower(prefix)));
        }


        private autoCompleterDeselect(prop: string): void {
            this.quoteSearch.searchModel[prop] = null;

            if(prop === 'externalAgentId') {
                this.selectedTravelAgent = undefined;
            }
            if(prop === 'userId') {
                this.selectedCustomer = undefined;
            }
            if(prop === 'quotedById') {
                this.selectedAgent = undefined;
            }
        }


        private getTravelAgentNameForQuote(quote: Quote.IQuoteSearchResult) {
            return this.quoteService.getTravelAgentNameForQuote(quote) || '-';
        }


        private getTravelAgentAgencyNameForQuote(quote: Quote.IQuoteSearchResult) {
            if (this.travelAgentDetails && this.travelAgentDetails[quote.TravelAgentId]) {
                return this.travelAgentDetails[quote.TravelAgentId].Name;
            }
            return '-';
        }


        private getCustomerNameForQuote(quote: Quote.IQuoteSearchResult): string {
            return this.quoteService.getCustomerNameForQuote(quote,
                this.getContactByUserId(quote.UserId)) || '-';
        }


        private getCustomerEmailForQuote(quote: Quote.IQuoteSearchResult): string {
            return this.quoteService.getCustomerEmailForQuote(quote,
                this.getContactByUserId(quote.UserId) ||
                    this.getTravelAgentContactById(quote.TravelAgentId)
            ) || '-';
        }


        private getCustomerPhoneNumberForQuote(quote: Quote.IQuoteSearchResult): string {
            return this.quoteService.getCustomerPhoneNumberForQuote(
                this.getContactByUserId(quote.UserId) ||
                    this.getTravelAgentContactById(quote.TravelAgentId) || quote
                ) || '-';
        }


        private getCustomerSkywardNumberForQuote(quote: Quote.IQuoteSearchResult): string {
            return this.quoteService.getCustomerSkywardNumberForQuote(
                this.getContactByUserId(quote.UserId) || quote) || '-';
        }


        private getContactByUserId(userId:string): any {
            if (userId && this.userDetails && this.userDetails[userId]) {
                return this.userDetails[userId].Contact;
            }
            return null;
        }


        private getTravelAgentContactById(agentId:number):any {
            if(agentId && this.travelAgentDetails && this.travelAgentDetails[agentId]) {
                return this.travelAgentDetails[agentId].Contact;
            }
            return null;
        }


        private hideSearch($e): void {
            $e.preventDefault();
            if (this.allowToggleAdvancedSearch) {
                this.hideAdvancedSearch();
            }
        }
    }


    angular
        .module('quote')
        .directive('advancedQuoteSearch', (): ng.IDirective => {
            return {
                restrict: 'E',
                scope: {},
                templateUrl: 'partials/quote/directivetemplate?type=advancedquotesearch',
                controller: AdvancedQuoteSearch,
                controllerAs: 'aqs',
                bindToController: {
                    hideAdvancedSearch: '&?',
                    showTravelAgencyDetail: '=?',
                    showLoyaltyNumber: '=?',
                    showLinkedBookings: '=?',
                }
            };
        });
}
